import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { toast } from 'sonner';
import { translateCountryDisplay, translateLanguage } from '../../utils/translations';
import {
  Upload,
  Download,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Trash2,
  RefreshCw,
  FileSpreadsheet,
  ArrowRight,
  FileDown,
  ChevronDown,
  X,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const GrowwerSyncTab = () => {
  const { language } = useLanguage();
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);
  const fileInputRef = useRef(null);

  // Bulk export state
  const [exportOpen, setExportOpen] = useState(false);
  const [filterOptions, setFilterOptions] = useState({ countries: [], categories: [], languages: [], site_types: [] });
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [countLoading, setCountLoading] = useState(false);
  const [countPreview, setCountPreview] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [excludeList, setExcludeList] = useState(null); // { comparison_id, domain_count, source_filename }
  const [uploadingExclude, setUploadingExclude] = useState(false);
  const excludeFileRef = useRef(null);
  const [exportFilters, setExportFilters] = useState({
    search: '',
    countries: [],
    categories: [],
    languages: [],
    site_types: [],
    dr_min: '',
    dr_max: '',
    traffic_min: '',
    traffic_max: '',
    price_min: '',
    price_max: '',
    exclude_in_growwer: false,
    only_active: false,
  });

  const t = language === 'es' ? {
    title: 'Sincronización con Growwer',
    description: 'Compara tu catálogo de KeyPubli con los sitios de Growwer para detectar diferencias.',
    uploadTitle: 'Subir Excel de Growwer',
    uploadDesc: 'Sube el archivo Excel con los sitios actualmente en Growwer',
    expectedColumns: 'Columnas esperadas: URL/Dominio, Precio Regular, Precio Sensible, Categorías',
    selectFile: 'Seleccionar archivo Excel',
    uploading: 'Procesando...',
    results: 'Resultados de la comparación',
    growwerSites: 'Sitios en Growwer',
    keypubliSites: 'Sitios en KeyPubli',
    newForGrowwer: 'Nuevos para Growwer',
    priceChanges: 'Cambios de Precio',
    deletedInKeypubli: 'Eliminados en KeyPubli',
    downloadExcel: 'Descargar Excel con Resultados',
    downloadDesc: 'El Excel incluye 4 hojas: Nuevos, Cambios de Precio, Eliminados y Resumen',
    newForGrowwerDesc: 'Sitios en KeyPubli que no están en Growwer (con precios -10%)',
    priceChangesDesc: 'Sitios con precios diferentes entre KeyPubli y Growwer',
    deletedDesc: 'Sitios en Growwer que ya no existen en KeyPubli',
    instructions: '¿Cómo funciona?',
    step1: 'Exporta tu lista de sitios de Growwer a Excel',
    step2: 'Sube el archivo aquí',
    step3: 'El sistema compara con tu catálogo de KeyPubli',
    step4: 'Descarga el Excel con las diferencias detectadas',
    excelFeatures: 'El Excel de resultados incluye:',
    feature1: 'Precios de KeyPubli y Growwer (-10%)',
    feature2: 'Precios con redacción por Growwer (-10€) y por KeyPubli',
    feature3: 'Enlaces NO DoFollow marcados en rojo',
    feature4: 'Etiquetas de patrocinado (SI/YES/ASK) marcadas en rojo',
    feature5: 'Sitios eliminados con acción sugerida',
    // Bulk export
    bulkExportBtn: 'Generar Excel para importar en Growwer',
    bulkExportTitle: 'Generar Excel de importación en Growwer',
    bulkExportDesc: 'Filtra tu catálogo y genera un Excel de 29 columnas listo para la subida masiva de Growwer (precios ajustados con -10%).',
    filterSearch: 'Buscar (uno o varios dominios / emails, separados por coma o salto de línea)',
    filterCountry: 'País',
    filterCategory: 'Categoría',
    filterLanguage: 'Idioma',
    filterSiteType: 'Tipo de sitio',
    filterDR: 'DR (min / max)',
    filterTraffic: 'Tráfico (min / max)',
    filterPrice: 'Precio PVP EUR (min / max)',
    onlyActive: 'Solo productos activos (permitidos por admin)',
    excludeGrowwer: 'Excluir dominios que Growwer ya tiene (usa la última comparación)',
    excludeGrowwerNoComp: 'Sube primero un Excel de Growwer arriba para poder excluir sus dominios.',
    previewCount: 'Previsualizar cantidad',
    generateDownload: 'Generar y descargar Excel',
    counting: 'Contando...',
    generating: 'Generando Excel...',
    matching: 'Coincidentes',
    alreadyInGrowwer: 'Ya en Growwer',
    willExport: 'Se exportarán',
    close: 'Cerrar',
    reset: 'Limpiar filtros',
    selected: 'seleccionado(s)',
    all: 'Todos',
    min: 'Mín',
    max: 'Máx',
  } : {
    title: 'Growwer Synchronization',
    description: 'Compare your KeyPubli catalog with Growwer sites to detect differences.',
    uploadTitle: 'Upload Growwer Excel',
    uploadDesc: 'Upload the Excel file with sites currently in Growwer',
    expectedColumns: 'Expected columns: URL/Domain, Regular Price, Sensitive Price, Categories',
    selectFile: 'Select Excel file',
    uploading: 'Processing...',
    results: 'Comparison Results',
    growwerSites: 'Sites in Growwer',
    keypubliSites: 'Sites in KeyPubli',
    newForGrowwer: 'New for Growwer',
    priceChanges: 'Price Changes',
    deletedInKeypubli: 'Deleted in KeyPubli',
    downloadExcel: 'Download Excel with Results',
    downloadDesc: 'Excel includes 4 sheets: New, Price Changes, Deleted and Summary',
    newForGrowwerDesc: 'Sites in KeyPubli not in Growwer (with -10% prices)',
    priceChangesDesc: 'Sites with different prices between KeyPubli and Growwer',
    deletedDesc: 'Sites in Growwer that no longer exist in KeyPubli',
    instructions: 'How does it work?',
    step1: 'Export your Growwer site list to Excel',
    step2: 'Upload the file here',
    step3: 'System compares with your KeyPubli catalog',
    step4: 'Download Excel with detected differences',
    excelFeatures: 'Result Excel includes:',
    feature1: 'KeyPubli and Growwer prices (-10%)',
    feature2: 'Writer pricing by Growwer (-€10) and by KeyPubli',
    feature3: 'Non-DoFollow links marked in red',
    feature4: 'Sponsored labels (SI/YES/ASK) marked in red',
    feature5: 'Deleted sites with suggested action',
    // Bulk export (EN)
    bulkExportBtn: 'Generate Excel to import in Growwer',
    bulkExportTitle: 'Generate Growwer bulk import Excel',
    bulkExportDesc: 'Filter your catalog and generate a 29-column Excel ready for Growwer bulk upload (prices adjusted with -10%).',
    filterSearch: 'Search (one or several domains / emails, separated by comma or newline)',
    filterCountry: 'Country',
    filterCategory: 'Category',
    filterLanguage: 'Language',
    filterSiteType: 'Site type',
    filterDR: 'DR (min / max)',
    filterTraffic: 'Traffic (min / max)',
    filterPrice: 'PVP price EUR (min / max)',
    onlyActive: 'Only active products (allowed by admin)',
    excludeGrowwer: 'Exclude domains already in Growwer (uses latest comparison)',
    excludeGrowwerNoComp: 'Upload a Growwer Excel above first to be able to exclude its domains.',
    previewCount: 'Preview count',
    generateDownload: 'Generate and download Excel',
    counting: 'Counting...',
    generating: 'Generating Excel...',
    matching: 'Matching',
    alreadyInGrowwer: 'Already in Growwer',
    willExport: 'Will export',
    close: 'Close',
    reset: 'Reset filters',
    selected: 'selected',
    all: 'All',
    min: 'Min',
    max: 'Max',
  };

  // --- Comparison history ---
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/growwer/comparisons?limit=20`);
      setHistory(res.data.comparisons || []);
      return res.data.comparisons || [];
    } catch (err) {
      console.error('history fetch error', err);
      return [];
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  // Load history + last comparison on mount
  useEffect(() => {
    fetchHistory().then(list => {
      if (list.length > 0 && !results) {
        // Auto-load most recent
        loadComparison(list[0].comparison_id);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadComparison = async (comparisonId) => {
    try {
      const res = await axios.get(`${API}/growwer/comparisons/${comparisonId}`);
      setResults(res.data);
      // Auto-enable exclude with this comparison
      setExportFilters(prev => ({ ...prev, exclude_in_growwer: true }));
    } catch (err) {
      console.error(err);
      toast.error(language === 'es' ? 'Error al cargar la comparación' : 'Error loading comparison');
    }
  };

  const deleteComparison = async (comparisonId) => {
    if (!window.confirm(language === 'es'
      ? '¿Eliminar esta comparación? No se puede deshacer.'
      : 'Delete this comparison? This cannot be undone.')) return;
    setDeletingId(comparisonId);
    try {
      await axios.delete(`${API}/growwer/comparisons/${comparisonId}`);
      toast.success(language === 'es' ? 'Comparación eliminada' : 'Comparison deleted');
      const remaining = history.filter(c => c.comparison_id !== comparisonId);
      setHistory(remaining);
      // If the deleted one was the currently loaded results, clear it
      if (results?.comparison_id === comparisonId) {
        if (remaining.length > 0) {
          loadComparison(remaining[0].comparison_id);
        } else {
          setResults(null);
        }
      }
    } catch (err) {
      console.error(err);
      toast.error(language === 'es' ? 'Error al eliminar' : 'Error deleting');
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleString(language === 'es' ? 'es-ES' : 'en-US', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    } catch { return iso; }
  };

  // --- Bulk export helpers ---
  useEffect(() => {
    if (!exportOpen) return;
    if (filterOptions.countries.length > 0) return; // already loaded
    setLoadingOptions(true);
    axios.get(`${API}/filters/options`)
      .then(res => {
        setFilterOptions({
          countries: res.data.countries || [],
          categories: res.data.categories || [],
          languages: res.data.languages || [],
          site_types: res.data.site_types || [],
        });
      })
      .catch(err => {
        console.error('filters/options error', err);
        toast.error(language === 'es' ? 'Error cargando opciones de filtro' : 'Error loading filter options');
      })
      .finally(() => setLoadingOptions(false));
  }, [exportOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const buildExportPayload = () => {
    const num = v => (v === '' || v === null || v === undefined ? null : Number(v));
    // Prefer the exclude-list uploaded inside the dialog; fall back to the compare results from the top panel.
    const excludeId = exportFilters.exclude_in_growwer
      ? (excludeList?.comparison_id || results?.comparison_id || null)
      : null;
    return {
      search: exportFilters.search || null,
      countries: exportFilters.countries,
      categories: exportFilters.categories,
      languages: exportFilters.languages,
      site_types: exportFilters.site_types,
      dr_min: num(exportFilters.dr_min),
      dr_max: num(exportFilters.dr_max),
      traffic_min: num(exportFilters.traffic_min),
      traffic_max: num(exportFilters.traffic_max),
      price_min: num(exportFilters.price_min),
      price_max: num(exportFilters.price_max),
      exclude_in_growwer: excludeId,
      only_active: exportFilters.only_active,
    };
  };

  const handleUploadExcludeList = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingExclude(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/growwer/exclude-list/upload`, formData);
      setExcludeList(res.data);
      // Auto-enable the exclude checkbox once we have a list
      setExportFilters(prev => ({ ...prev, exclude_in_growwer: true }));
      setCountPreview(null);
      toast.success(
        language === 'es'
          ? `✓ ${res.data.domain_count.toLocaleString()} dominios cargados de Growwer`
          : `✓ ${res.data.domain_count.toLocaleString()} Growwer domains loaded`
      );
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || (language === 'es' ? 'Error al procesar el archivo' : 'Error processing file'));
    } finally {
      setUploadingExclude(false);
      if (excludeFileRef.current) excludeFileRef.current.value = '';
    }
  };

  const clearExcludeList = () => {
    setExcludeList(null);
    setExportFilters(prev => ({ ...prev, exclude_in_growwer: false }));
    setCountPreview(null);
  };

  const handlePreviewCount = async () => {
    setCountLoading(true);
    setCountPreview(null);
    try {
      const res = await axios.post(`${API}/growwer/bulk-export/count`, buildExportPayload());
      setCountPreview(res.data);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || (language === 'es' ? 'Error al contar' : 'Count error'));
    } finally {
      setCountLoading(false);
    }
  };

  const handleGenerateExport = async () => {
    setDownloading(true);
    try {
      const res = await axios.post(`${API}/growwer/bulk-export`, buildExportPayload(), {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      const today = new Date().toISOString().split('T')[0];
      link.href = url;
      link.setAttribute('download', `growwer_bulk_${today}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(language === 'es' ? '¡Excel de Growwer generado!' : 'Growwer Excel generated!');
    } catch (err) {
      console.error(err);
      // Blob responses need to be parsed differently
      let msg = language === 'es' ? 'Error al generar Excel' : 'Error generating Excel';
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed?.detail) msg = parsed.detail;
        } catch { /* ignore */ }
      }
      toast.error(msg);
    } finally {
      setDownloading(false);
    }
  };

  const resetExportFilters = () => {
    setExportFilters({
      search: '', countries: [], categories: [], languages: [], site_types: [],
      dr_min: '', dr_max: '', traffic_min: '', traffic_max: '', price_min: '', price_max: '',
      exclude_in_growwer: false, only_active: false,
    });
    setCountPreview(null);
  };

  const toggleArrayValue = (key, value) => {
    setExportFilters(prev => {
      const arr = prev[key] || [];
      const next = arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value];
      return { ...prev, [key]: next };
    });
    setCountPreview(null);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setResults(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/growwer/compare`, formData);
      setResults(response.data);
      // Auto-enable the "exclude Growwer domains" flag so the next bulk export uses this list.
      setExportFilters(prev => ({ ...prev, exclude_in_growwer: true }));
      // Refresh history so the new comparison appears in the list
      fetchHistory();
      toast.success(language === 'es' ? '¡Comparación completada!' : 'Comparison completed!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Error al procesar archivo');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDownload = async () => {
    if (!results?.comparison_id) return;

    try {
      const response = await axios.get(`${API}/growwer/export/${results.comparison_id}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `growwer_sync_${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(language === 'es' ? '¡Excel descargado!' : 'Excel downloaded!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error(language === 'es' ? 'Error al descargar' : 'Download error');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <RefreshCw className="h-6 w-6" />
            {t.title}
          </h2>
          <p className="text-muted-foreground">{t.description}</p>
        </div>
        <Button
          data-testid="growwer-bulk-export-open-btn"
          onClick={() => setExportOpen(true)}
          className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
        >
          <FileDown className="h-4 w-4 mr-2" />
          {t.bulkExportBtn}
        </Button>
      </div>

      {/* Instructions */}
      <Card className="border-blue-200 bg-blue-50/50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileSpreadsheet className="h-5 w-5 text-blue-600" />
            </div>
            <div className="space-y-2 flex-1">
              <h4 className="font-medium text-blue-900">{t.instructions}</h4>
              <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                <li>{t.step1}</li>
                <li>{t.step2}</li>
                <li>{t.step3}</li>
                <li>{t.step4}</li>
              </ol>
              <div className="mt-3 pt-3 border-t border-blue-200">
                <p className="text-sm font-medium text-blue-900">{t.excelFeatures}</p>
                <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside mt-1">
                  <li>{t.feature1}</li>
                  <li>{t.feature2}</li>
                  <li>{t.feature3}</li>
                  <li>{t.feature4}</li>
                  <li>{t.feature5}</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Upload Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            {t.uploadTitle}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-2">{t.uploadDesc}</p>
          <p className="text-xs text-muted-foreground mb-4">{t.expectedColumns}</p>
          
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={handleFileUpload}
            disabled={uploading}
          />
          
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="w-full h-24 border-dashed border-2"
          >
            {uploading ? (
              <div className="flex flex-col items-center">
                <Loader2 className="h-8 w-8 mb-2 animate-spin" />
                <span>{t.uploading}</span>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <Upload className="h-8 w-8 mb-2 text-muted-foreground" />
                <span>{t.selectFile}</span>
              </div>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* History of comparisons */}
      {history.length > 0 && (
        <Card data-testid="growwer-history-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <FileSpreadsheet className="h-4 w-4" />
              {language === 'es' ? 'Historial de comparaciones' : 'Comparison history'}
              <Badge variant="secondary" className="ml-1">{history.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {history.map((h) => {
                const isActive = results?.comparison_id === h.comparison_id;
                return (
                  <div
                    key={h.comparison_id}
                    className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${
                      isActive ? 'bg-emerald-50 border-emerald-300' : 'bg-white hover:bg-slate-50'
                    }`}
                    data-testid={`growwer-history-item-${h.comparison_id}`}
                  >
                    <button
                      className="flex-1 min-w-0 text-left"
                      onClick={() => loadComparison(h.comparison_id)}
                      disabled={isActive}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        {isActive && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />}
                        <span className="font-medium truncate">
                          {h.source_filename || h.comparison_id}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
                        <span>{formatDate(h.created_at)}</span>
                        <span>·</span>
                        <span>{(h.growwer_count || 0).toLocaleString()} Growwer</span>
                        <span>·</span>
                        <span className="text-green-700">{(h.stats?.new_count || 0).toLocaleString()} nuevos</span>
                        <span>·</span>
                        <span className="text-yellow-700">{(h.stats?.changes_count || 0).toLocaleString()} cambios</span>
                      </div>
                    </button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); deleteComparison(h.comparison_id); }}
                      disabled={deletingId === h.comparison_id}
                      data-testid={`growwer-history-delete-${h.comparison_id}`}
                    >
                      {deletingId === h.comparison_id
                        ? <Loader2 className="h-4 w-4 animate-spin" />
                        : <Trash2 className="h-4 w-4 text-red-500" />}
                    </Button>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {results && (
        <div className="space-y-4">
          {/* Loaded file info bar */}
          {(results.source_filename || results.created_at) && (
            <div className="flex items-center justify-between gap-3 bg-slate-100 border rounded-lg px-3 py-2 text-sm" data-testid="growwer-loaded-info">
              <div className="flex items-center gap-2 min-w-0">
                <FileSpreadsheet className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="font-medium truncate">{results.source_filename || (language === 'es' ? 'Comparación' : 'Comparison')}</span>
                {results.created_at && (
                  <span className="text-muted-foreground shrink-0">· {formatDate(results.created_at)}</span>
                )}
              </div>
              {results.comparison_id && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => deleteComparison(results.comparison_id)}
                  disabled={deletingId === results.comparison_id}
                  data-testid="growwer-delete-current-btn"
                >
                  {deletingId === results.comparison_id
                    ? <Loader2 className="h-4 w-4 animate-spin" />
                    : <Trash2 className="h-4 w-4 text-red-600" />}
                </Button>
              )}
            </div>
          )}

          <h3 className="text-lg font-semibold flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            {t.results}
          </h3>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className="bg-slate-50">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold">{results.stats.growwer_sites}</p>
                <p className="text-xs text-muted-foreground">{t.growwerSites}</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-50">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold">{results.stats.keypubli_sites}</p>
                <p className="text-xs text-muted-foreground">{t.keypubliSites}</p>
              </CardContent>
            </Card>
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-green-600">{results.stats.new_for_growwer}</p>
                <p className="text-xs text-green-600">{t.newForGrowwer}</p>
              </CardContent>
            </Card>
            <Card className="bg-yellow-50 border-yellow-200">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-yellow-600">{results.stats.price_changes}</p>
                <p className="text-xs text-yellow-600">{t.priceChanges}</p>
              </CardContent>
            </Card>
            <Card className="bg-red-50 border-red-200">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-red-600">{results.stats.deleted_in_keypubli}</p>
                <p className="text-xs text-red-600">{t.deletedInKeypubli}</p>
              </CardContent>
            </Card>
          </div>

          {/* Explanation */}
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div className="flex items-start gap-2 p-3 bg-green-50 rounded-lg">
              <ArrowRight className="h-4 w-4 text-green-600 mt-0.5" />
              <div>
                <p className="font-medium text-green-800">{t.newForGrowwer}</p>
                <p className="text-green-700">{t.newForGrowwerDesc}</p>
              </div>
            </div>
            <div className="flex items-start gap-2 p-3 bg-yellow-50 rounded-lg">
              <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5" />
              <div>
                <p className="font-medium text-yellow-800">{t.priceChanges}</p>
                <p className="text-yellow-700">{t.priceChangesDesc}</p>
              </div>
            </div>
            <div className="flex items-start gap-2 p-3 bg-red-50 rounded-lg">
              <Trash2 className="h-4 w-4 text-red-600 mt-0.5" />
              <div>
                <p className="font-medium text-red-800">{t.deletedInKeypubli}</p>
                <p className="text-red-700">{t.deletedDesc}</p>
              </div>
            </div>
          </div>

          {/* Download Button */}
          <Card className="bg-gradient-to-r from-green-50 to-blue-50 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{t.downloadExcel}</p>
                  <p className="text-sm text-muted-foreground">{t.downloadDesc}</p>
                </div>
                <Button onClick={handleDownload} className="bg-green-600 hover:bg-green-700">
                  <Download className="h-4 w-4 mr-2" />
                  {language === 'es' ? 'Descargar' : 'Download'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      {/* Bulk Export Dialog */}
      <Dialog open={exportOpen} onOpenChange={(o) => { setExportOpen(o); if (!o) setCountPreview(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="growwer-bulk-export-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileDown className="h-5 w-5 text-emerald-600" />
              {t.bulkExportTitle}
            </DialogTitle>
            <DialogDescription>{t.bulkExportDesc}</DialogDescription>
          </DialogHeader>

          {loadingOptions ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <div className="space-y-4 py-2">
              {/* Search */}
              <div>
                <Label htmlFor="grw-search">{t.filterSearch}</Label>
                <Textarea
                  id="grw-search"
                  data-testid="growwer-export-search"
                  value={exportFilters.search}
                  onChange={(e) => { setExportFilters({ ...exportFilters, search: e.target.value }); setCountPreview(null); }}
                  placeholder={language === 'es'
                    ? 'ejemplo.com, otro@sitio.es\neditor@medio.com'
                    : 'example.com, editor@site.es\neditor@media.com'}
                  rows={3}
                  className="font-mono text-sm"
                />
              </div>

              {/* Multi-select filters */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <MultiSelect
                  label={t.filterCountry}
                  options={filterOptions.countries}
                  selected={exportFilters.countries}
                  onToggle={(v) => toggleArrayValue('countries', v)}
                  testId="growwer-export-countries"
                  tAll={t.all}
                  tSelected={t.selected}
                  formatLabel={(v) => translateCountryDisplay(v, language)}
                />
                <MultiSelect
                  label={t.filterCategory}
                  options={filterOptions.categories}
                  selected={exportFilters.categories}
                  onToggle={(v) => toggleArrayValue('categories', v)}
                  testId="growwer-export-categories"
                  tAll={t.all}
                  tSelected={t.selected}
                />
                <MultiSelect
                  label={t.filterLanguage}
                  options={filterOptions.languages}
                  selected={exportFilters.languages}
                  onToggle={(v) => toggleArrayValue('languages', v)}
                  testId="growwer-export-languages"
                  tAll={t.all}
                  tSelected={t.selected}
                  formatLabel={(v) => translateLanguage(v, language)}
                />
                <MultiSelect
                  label={t.filterSiteType}
                  options={filterOptions.site_types}
                  selected={exportFilters.site_types}
                  onToggle={(v) => toggleArrayValue('site_types', v)}
                  testId="growwer-export-sitetypes"
                  tAll={t.all}
                  tSelected={t.selected}
                />
              </div>

              {/* Range filters */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label>{t.filterDR}</Label>
                  <div className="flex gap-2">
                    <Input type="number" min="0" placeholder={t.min} data-testid="growwer-export-dr-min"
                      value={exportFilters.dr_min}
                      onChange={(e) => { setExportFilters({ ...exportFilters, dr_min: e.target.value }); setCountPreview(null); }} />
                    <Input type="number" min="0" placeholder={t.max} data-testid="growwer-export-dr-max"
                      value={exportFilters.dr_max}
                      onChange={(e) => { setExportFilters({ ...exportFilters, dr_max: e.target.value }); setCountPreview(null); }} />
                  </div>
                </div>
                <div>
                  <Label>{t.filterTraffic}</Label>
                  <div className="flex gap-2">
                    <Input type="number" min="0" placeholder={t.min} data-testid="growwer-export-traffic-min"
                      value={exportFilters.traffic_min}
                      onChange={(e) => { setExportFilters({ ...exportFilters, traffic_min: e.target.value }); setCountPreview(null); }} />
                    <Input type="number" min="0" placeholder={t.max} data-testid="growwer-export-traffic-max"
                      value={exportFilters.traffic_max}
                      onChange={(e) => { setExportFilters({ ...exportFilters, traffic_max: e.target.value }); setCountPreview(null); }} />
                  </div>
                </div>
                <div>
                  <Label>{t.filterPrice}</Label>
                  <div className="flex gap-2">
                    <Input type="number" min="0" placeholder={t.min} data-testid="growwer-export-price-min"
                      value={exportFilters.price_min}
                      onChange={(e) => { setExportFilters({ ...exportFilters, price_min: e.target.value }); setCountPreview(null); }} />
                    <Input type="number" min="0" placeholder={t.max} data-testid="growwer-export-price-max"
                      value={exportFilters.price_max}
                      onChange={(e) => { setExportFilters({ ...exportFilters, price_max: e.target.value }); setCountPreview(null); }} />
                  </div>
                </div>
              </div>

              {/* Toggles */}
              <div className="space-y-3 border-t pt-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    data-testid="growwer-export-only-active"
                    checked={exportFilters.only_active}
                    onCheckedChange={(v) => { setExportFilters({ ...exportFilters, only_active: !!v }); setCountPreview(null); }}
                  />
                  <span className="text-sm">{t.onlyActive}</span>
                </label>

                {/* Exclude-in-Growwer block: upload-in-dialog + auto-checkbox */}
                <div className="border rounded-lg p-3 bg-slate-50 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="text-sm font-medium">
                        {language === 'es' ? 'Excluir sitios que Growwer ya tiene' : 'Exclude sites Growwer already has'}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {language === 'es'
                          ? 'Sube tu Excel actual de Growwer (basta con la columna URL) y esos dominios se descartarán del export.'
                          : 'Upload your current Growwer Excel (URL column is enough); those domains will be excluded from the export.'}
                      </div>
                    </div>
                  </div>

                  {excludeList ? (
                    <div className="flex items-center justify-between gap-2 bg-emerald-50 border border-emerald-200 rounded p-2">
                      <div className="text-sm text-emerald-900">
                        <span className="font-medium">✓ {excludeList.domain_count.toLocaleString()}</span>{' '}
                        {language === 'es' ? 'dominios cargados' : 'domains loaded'}
                        {excludeList.source_filename && (
                          <span className="text-xs text-emerald-700 block truncate max-w-xs">
                            {excludeList.source_filename}
                          </span>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearExcludeList}
                        data-testid="growwer-exclude-list-clear"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : results?.comparison_id ? (
                    <div className="flex items-center justify-between gap-2 bg-blue-50 border border-blue-200 rounded p-2">
                      <div className="text-sm text-blue-900">
                        {language === 'es'
                          ? `Usando comparación cargada arriba (${(results.stats?.growwer_sites || 0).toLocaleString()} dominios)`
                          : `Using comparison loaded above (${(results.stats?.growwer_sites || 0).toLocaleString()} domains)`}
                      </div>
                    </div>
                  ) : (
                    <>
                      <input
                        ref={excludeFileRef}
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        className="hidden"
                        onChange={handleUploadExcludeList}
                        disabled={uploadingExclude}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => excludeFileRef.current?.click()}
                        disabled={uploadingExclude}
                        className="w-full"
                        data-testid="growwer-exclude-list-upload"
                      >
                        {uploadingExclude ? (
                          <><Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          {language === 'es' ? 'Procesando...' : 'Processing...'}</>
                        ) : (
                          <><Upload className="h-4 w-4 mr-2" />
                          {language === 'es' ? 'Subir Excel de Growwer aquí' : 'Upload Growwer Excel here'}</>
                        )}
                      </Button>
                    </>
                  )}

                  {(excludeList || results?.comparison_id) && (
                    <label className="flex items-center gap-2 cursor-pointer pt-1">
                      <Checkbox
                        data-testid="growwer-export-exclude-growwer"
                        checked={exportFilters.exclude_in_growwer}
                        onCheckedChange={(v) => { setExportFilters({ ...exportFilters, exclude_in_growwer: !!v }); setCountPreview(null); }}
                      />
                      <span className="text-sm">
                        {language === 'es' ? 'Aplicar exclusión al generar el Excel' : 'Apply exclusion when generating Excel'}
                      </span>
                    </label>
                  )}
                </div>
              </div>

              {/* Count preview result */}
              {countPreview && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 bg-slate-50 rounded-lg p-3 border" data-testid="growwer-export-count-preview">
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground">{t.matching}</p>
                    <p className="text-xl font-bold">{countPreview.total_matching?.toLocaleString?.() ?? countPreview.total_matching}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground">{t.alreadyInGrowwer}</p>
                    <p className="text-xl font-bold text-yellow-600">{(countPreview.growwer_already_has ?? 0).toLocaleString?.()}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground">
                      {language === 'es' ? 'Excluidos por overlap' : 'Excluded (overlap)'}
                    </p>
                    <p className="text-xl font-bold text-orange-600">{(countPreview.overlap_excluded ?? 0).toLocaleString?.()}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground">{t.willExport}</p>
                    <p className="text-xl font-bold text-emerald-600">{(countPreview.will_export_estimate ?? 0).toLocaleString?.()}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="ghost" onClick={resetExportFilters} data-testid="growwer-export-reset-btn">
              <X className="h-4 w-4 mr-1" /> {t.reset}
            </Button>
            <div className="flex-1" />
            <Button
              variant="outline"
              onClick={handlePreviewCount}
              disabled={countLoading || downloading}
              data-testid="growwer-export-count-btn"
            >
              {countLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {countLoading ? t.counting : t.previewCount}
            </Button>
            <Button
              onClick={handleGenerateExport}
              disabled={downloading || countLoading}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="growwer-export-generate-btn"
            >
              {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              {downloading ? t.generating : t.generateDownload}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Compact multi-select using Popover + Checkbox list
const MultiSelect = ({ label, options, selected, onToggle, testId, tAll, tSelected, formatLabel }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const fmt = (v) => (formatLabel ? formatLabel(v) : String(v));

  // Sort options by displayed label (case-insensitive)
  const sortedOptions = [...options].sort((a, b) => fmt(a).toLowerCase().localeCompare(fmt(b).toLowerCase()));

  const filtered = q.trim()
    ? sortedOptions.filter(o => {
        const ql = q.toLowerCase();
        return String(o).toLowerCase().includes(ql) || fmt(o).toLowerCase().includes(ql);
      })
    : sortedOptions;

  const displayLabel = selected.length === 0
    ? tAll
    : selected.length === 1
      ? fmt(selected[0])
      : `${selected.length} ${tSelected}`;
  return (
    <div>
      <Label>{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            className="w-full justify-between font-normal"
            data-testid={testId}
          >
            <span className="truncate">{displayLabel}</span>
            <ChevronDown className="h-4 w-4 opacity-50 shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <div className="p-2 border-b">
            <Input
              placeholder="..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="h-8"
              data-testid={`${testId}-search`}
            />
          </div>
          <div className="max-h-64 overflow-y-auto p-1">
            {filtered.length === 0 && (
              <div className="text-sm text-muted-foreground text-center py-4">—</div>
            )}
            {filtered.map(opt => (
              <label
                key={opt}
                className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-100 cursor-pointer text-sm"
              >
                <Checkbox
                  checked={selected.includes(opt)}
                  onCheckedChange={() => onToggle(opt)}
                />
                <span className="truncate">{fmt(opt)}</span>
              </label>
            ))}
          </div>
          {selected.length > 0 && (
            <div className="p-2 border-t">
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-xs"
                onClick={() => selected.forEach(v => onToggle(v))}
              >
                <X className="h-3 w-3 mr-1" /> Limpiar
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default GrowwerSyncTab;
